import React from 'react';
import GridLayout, { CardData } from '@/components/layout/element-layout/grid-layout/GridLayout';
import { FilterConfig, FilterActions, DataViewOption } from '@/components/cards/widgets/TableFilter';

export interface FolderGroup {
  folderId: string | null;
  folderName: string;
  cards: CardData[];
}

export interface GridWidgetProps {
  title?: string;
  cards?: CardData[];
  groupedCards?: FolderGroup[];
  onCardClick?: (card: CardData) => void;
  onDelete?: ((id: string, gameId: string) => void) | ((row: Record<string, unknown>) => void);
  onEdit?: ((id: string) => void) | ((row: Record<string, unknown>) => void);
  onAdd?: () => void;
  onBulkAdd?: () => void;
  showHeader?: boolean;
  showSearch?: boolean;
  showTagFilters?: boolean;
  showGridSizeSelector?: boolean;
  backgroundColor?: string;
  selectedFolderId?: string | null;
  filterConfig?: FilterConfig[];
  filterActions?: Partial<FilterActions>;
  itemType?: string;
  // Data view mode props
  availableSections?: DataViewOption[];
  availableFields?: DataViewOption[];
  rawItems?: any[];
  templateLookup?: Record<string, { id: string; name: string; color?: string }>;
  // Callback when a cell value changes in table view
  onCellValueChange?: (rowKey: string, cellKey: string, newValue: any) => void;
  // Pending changes props
  pendingChangesCount?: number;
  hasPendingChanges?: boolean;
  modifiedItems?: Set<string>;
  modifiedCells?: Map<string, string[]>;
  onSaveItem?: (itemId: string) => void;
  onDiscardItem?: (itemId: string) => void;
  onSaveAllItems?: () => Promise<{ success: boolean; conflicts: any[] }>;
  onDiscardAllItems?: () => void;
  onForceSaveItem?: (itemId: string) => Promise<boolean>;
  // Folder path lookup for displaying folder paths instead of IDs
  folderPathLookup?: (folderId: string | null | undefined) => string;
  // Bulk delete handler for selected items
  onDeleteSelected?: (ids: string[]) => Promise<void>;
}

export default function GridWidget({
  title = 'Grid',
  cards = [],
  groupedCards,
  onCardClick,
  onDelete,
  onEdit,
  onAdd,
  onBulkAdd,
  showHeader = false,
  showSearch = true,
  showTagFilters = true,
  showGridSizeSelector = true,
  backgroundColor,
  selectedFolderId,
  filterConfig,
  filterActions,
  itemType,
  availableSections,
  availableFields,
  rawItems,
  templateLookup,
  onCellValueChange,
  // Pending changes props
  pendingChangesCount,
  hasPendingChanges,
  modifiedItems,
  modifiedCells,
  onSaveItem,
  onDiscardItem,
  onSaveAllItems,
  onDiscardAllItems,
  onForceSaveItem,
  folderPathLookup,
  // Bulk delete
  onDeleteSelected,
}: GridWidgetProps) {
  // Create adapter functions to handle different handler signatures
  // Pass through the standard (id, gameId) signature directly
  const handleDelete = React.useMemo(() => {
    if (!onDelete) return undefined;
    
    // Check if the onDelete function expects 2 parameters (standard signature)
    // or 1 parameter (row object signature)
    if (onDelete.length === 2) {
      // Standard signature: (id: string, gameId: string) => void
      return onDelete as (id: string, gameId: string) => void;
    } else {
      // Row object signature: (row: Record<string, unknown>) => void
      return (id: string, gameId: string) => {
        const card = cards.find(c => c.id === id);
        if (card) {
          (onDelete as (row: Record<string, unknown>) => void)({
            id,
            gameId,
            title: card.title,
            ...card
          });
        }
      };
    }
  }, [onDelete, cards]);

  const handleEdit = React.useMemo(() => {
    if (!onEdit) return undefined;
    
    return (id: string) => {
      const card = cards.find(c => c.id === id);
      if (card) {
        // Since DocumentsPage handlers expect a row object, always pass the full card object
        (onEdit as (row: Record<string, unknown>) => void)({
          id,
          gameId: card.gameId,
          title: card.title,
          ...card
        });
      }
    };
  }, [onEdit, cards]);

  return (
    <div 
      className="h-full w-full flex flex-col"
    >
      <GridLayout
        title={title}
        cards={cards}
        groupedCards={groupedCards}
        onCardClick={onCardClick}
        onDelete={handleDelete}
        onEdit={handleEdit}
        showHeader={showHeader}
        selectedFolderId={selectedFolderId}
        filterConfig={filterConfig}
        filterActions={filterActions}
        itemType={itemType}
        availableSections={availableSections}
        availableFields={availableFields}
        rawItems={rawItems}
        templateLookup={templateLookup}
        onCellValueChange={onCellValueChange}
        // Pending changes props
        pendingChangesCount={pendingChangesCount}
        hasPendingChanges={hasPendingChanges}
        modifiedItems={modifiedItems}
        modifiedCells={modifiedCells}
        onSaveItem={onSaveItem}
        onDiscardItem={onDiscardItem}
        onSaveAllItems={onSaveAllItems}
        onDiscardAllItems={onDiscardAllItems}
        onForceSaveItem={onForceSaveItem}
        folderPathLookup={folderPathLookup}
        // Bulk delete
        onDeleteSelected={onDeleteSelected}
      />
    </div>
  );
} 